import axios from 'axios';

// DISABLED: Unresponsive first load fix - session monitoring
// import sessionMonitor from '@/utils/session-monitor';
import { sanitizePath } from '../http/sanitize-path';
import { recordRequest } from '../observability/request-log';
import { loadGQL } from '../queries/gql-loader';
import { viewAsHeaderValue } from '../store/view-as';
import {
  authTool,
  clearAuthentication,
  clearSessionCache,
  getCachedSessionData,
  getClientSessionData,
  getCookieStorageFromServer,
  updateServerCookie,
} from '../utils/auth-utils';
import { isServer } from '../utils/helpers';
import { isCorsMaskedAuthFailure, shouldAttemptJwtRefresh } from '../utils/is-expired-jwt-error';
import { serverLogger } from '../utils/logger';
import { getTokenRefreshManager } from '../utils/token-refresh-manager';
import { updateAppSession } from './app-api';

let httpMetricsPromise: Promise<typeof import('./http-metrics')> | null = null;

/**
 * Client metric beacons, batched.
 *
 * These are observability, not product data, but one beacon per API response
 * means a screen that makes sixty calls also makes sixty POSTs. `sendBeacon`
 * does not block the page, but each one still takes a connection from the same
 * small pool the real requests are queuing for, spends uplink (the scarce
 * direction on mobile), and costs an iron-session decrypt server-side. Measured
 * on the BackOffice website dashboard: 59 beacons for one page load.
 *
 * Buffer instead, and send one beacon per batch — on a size cap, a short timer,
 * or the page going away, whichever comes first. Nothing is dropped: `pagehide`
 * and `visibilitychange` are exactly the events `sendBeacon` exists for.
 */
interface MetricSample {
  path: string;
  status: number;
  elapsedMs: number;
}

const METRIC_BATCH_MAX = 25;
const METRIC_FLUSH_MS = 4000;

let metricBuffer: MetricSample[] = [];
let metricTimer: ReturnType<typeof setTimeout> | null = null;
let metricUnloadBound = false;

function flushMetrics(): void {
  if (metricTimer) {
    clearTimeout(metricTimer);
    metricTimer = null;
  }
  if (metricBuffer.length === 0) return;
  const samples = metricBuffer;
  metricBuffer = [];
  try {
    const body = JSON.stringify({ samples });
    navigator.sendBeacon('/api/metrics', new Blob([body], { type: 'application/json' }));
  } catch {
    // Ignore logging failures
  }
}

function queueMetric(sample: MetricSample): void {
  if (typeof navigator === 'undefined' || typeof navigator.sendBeacon !== 'function') return;
  metricBuffer.push(sample);

  if (!metricUnloadBound && typeof document !== 'undefined') {
    metricUnloadBound = true;
    // `pagehide` is the one that fires reliably on mobile Safari, where a tab can
    // be frozen or discarded without `beforeunload` ever running.
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') flushMetrics();
    });
    window.addEventListener('pagehide', flushMetrics);
  }

  if (metricBuffer.length >= METRIC_BATCH_MAX) {
    flushMetrics();
    return;
  }
  if (!metricTimer) metricTimer = setTimeout(flushMetrics, METRIC_FLUSH_MS);
}

async function recordMetrics(config: any, status: number): Promise<void> {
  const rawUrl = config?.url || '';
  const path = sanitizePath(rawUrl);
  const onClient = !isServer();
  // Single client-side recording point for the support observability buffer (success + error
  // both flow through here), so the sanitized path is computed once per response.
  if (onClient) recordRequest({ method: (config?.method || 'GET').toUpperCase(), path, status });

  const start = config?.metadata?.start;
  if (typeof start !== 'number') return;
  const elapsedMs = Date.now() - start;
  if (onClient) {
    queueMetric({ path, status, elapsedMs });
    return;
  }
  try {
    if (!httpMetricsPromise) {
      httpMetricsPromise = import('./http-metrics');
    }
    const { recordHttpMetrics } = await httpMetricsPromise;
    await recordHttpMetrics(path, status, elapsedMs);
  } catch {
    // Ignore logging failures
  }
}

const createHttpClient = (baseUrl: string) => {
  const httpClient = axios.create({
    baseURL: baseUrl,
  });

  httpClient.interceptors.request.use(async function (config) {
    config.metadata = { ...(config.metadata || {}), start: Date.now() };

    // "View as" lens: while an admin previews the BackOffice as a member,
    // every request carries the lens groups. The backend honors the header
    // only after verifying the real caller is owner/SUPER_ADMIN, and it only
    // ever narrows what a request returns — inert everywhere else.
    if (!isServer()) {
      try {
        const lens = viewAsHeaderValue();
        if (lens) config.headers['x-view-as-groups'] = lens;
      } catch {
        // The lens is a convenience — never block a request over it.
      }
    }

    // An explicitly-set bearer always wins over the session read: callers like
    // getWebsiteById pass a shared guest token for anonymous published-page
    // renders where no session cookie exists at all (cookie-less crawler hits,
    // CDN-cacheable requests). This also covers the 401-refresh retry, where
    // the response interceptor already set a fresh token.
    if (config.headers?.Authorization) {
      return config;
    }

    let cookiesData;
    if (isServer()) {
      cookiesData = await getCookieStorageFromServer();
    } else {
      cookiesData = getCachedSessionData();
      if (!cookiesData) {
        cookiesData = await getClientSessionData();
      }
    }
    config.headers.Authorization = cookiesData ? `Bearer ${cookiesData?.jwt}` : '';
    return config;
  });

  httpClient.interceptors.response.use(
    (response) => {
      void recordMetrics(response.config, response.status);
      return response;
    },
    async function (error: any) {
      if (error?.config) {
        void recordMetrics(error.config, error.response?.status ?? 0);
      }
      const originalRequest = error.config;
      const cookiesData = isServer()
        ? await getCookieStorageFromServer()
        : getCachedSessionData() || (await getClientSessionData());

      if (error) {
        const status = error.response?.status;
        const path = sanitizePath(originalRequest?.url || '');
        const method = originalRequest?.method?.toUpperCase();
        const correlationId =
          originalRequest?.headers?.['x-correlation-id'] || originalRequest?.headers?.['X-Correlation-Id'];
        const retry = !originalRequest?._retry;
        const logMeta = {
          message: error?.message,
          status,
          errMessage: error.response?.data?.err?.message,
          detail: error.response?.data?.detail,
          retry,
          path,
          method,
          correlationId,
        };

        if (status >= 500) {
          serverLogger.error('api error', logMeta);
        } else if (status >= 400) {
          serverLogger.warn('api error', logMeta);
        } else {
          serverLogger.error('api error', logMeta);
        }

        // DISABLED: Unresponsive first load fix - session monitor tracking
        // if (!isServer()) {
        //   sessionMonitor.recordAuthFailure(error);
        // }
      }

      // Skip token refresh for session update calls to prevent infinite loop
      const isSessionUpdate = originalRequest?.url?.includes('/api/session');

      if (isSessionUpdate && (error.response?.status === 401 || error.response?.status === 403)) {
        serverLogger.debug('[http-client] Skipping token refresh for /api/session call to prevent loop');
      }

      // A YouRAI auth rejection can arrive with no response at all: that API
      // omits Access-Control-Allow-Origin on its 401s, so the browser blocks
      // the answer and axios reports only "Network Error". Status-driven
      // detection sees nothing to refresh on, the session is never renewed,
      // and every later request fails identically until a full page reload —
      // the "everything suddenly stopped saving after an hour" report. When
      // our own bearer is already past `exp`, that is what this is.
      const corsMaskedAuthFailure = isCorsMaskedAuthFailure(error, cookiesData?.jwt);
      if (corsMaskedAuthFailure) {
        serverLogger.warn('api error auth failure masked by missing CORS headers - refreshing anyway', {
          path: sanitizePath(originalRequest?.url || ''),
          method: originalRequest?.method?.toUpperCase(),
        });
      }
      const shouldRefresh =
        shouldAttemptJwtRefresh(error.response?.status, error.response?.data) || corsMaskedAuthFailure;

      if (
        shouldRefresh &&
        !originalRequest?._retry &&
        !originalRequest?.url?.includes('getJwtTokenV2') &&
        !isSessionUpdate // Don't retry session updates to prevent loops
      ) {
        serverLogger.debug('api error attempting token refresh');

        originalRequest._retry = true;

        // SECURITY FIX: Get user-scoped token refresh manager
        // This prevents one user's refresh failures from blocking another user
        const manager = getTokenRefreshManager(cookiesData?.userId, cookiesData?.tenantId);

        let refreshSuccess: boolean;
        if (manager.isRefreshing()) {
          serverLogger.debug('[http-client] Token refresh already in progress, waiting');
          refreshSuccess = await manager.executeRefresh(async () => true);
        } else {
          // Use the token refresh manager to coordinate refresh attempts
          refreshSuccess = await manager.executeRefresh(async () => {
            let accessToken: any;

            // Check if user is a guest - if so, don't attempt refresh, get new guest token instead
            if (cookiesData?.role === 'guest') {
              serverLogger.debug('api error guest token expired - getting new guest token');
              // Clear authentication and trigger new guest token flow
              clearAuthentication();
              authTool.sendAuthExpired();
              // Get new guest token through the normal initialization flow
              const { getTenantInfoJwt } = await loadGQL();
              const newGuestToken = await getTenantInfoJwt(cookiesData?.tenantId || '');
              if (newGuestToken?.data?.getTenantInfo) {
                accessToken = {
                  data: {
                    getJwtTokenV2: {
                      jwt: newGuestToken.data.getTenantInfo.jwt,
                      refreshToken: newGuestToken.data.getTenantInfo.refreshToken,
                    },
                  },
                };
              }
            } else if (cookiesData?.role && cookiesData?.role !== 'guest') {
              // Authenticated user - attempt to refresh token
              if (cookiesData?.refreshToken && cookiesData?.tenantId) {
                serverLogger.debug('api error getNewJwt for authenticated user with refresh token');
                try {
                  // Pass the expired JWT to be used in authorization header
                  const { getNewJwt } = await loadGQL();
                  accessToken = await getNewJwt(
                    cookiesData?.tenantId || '',
                    cookiesData?.refreshToken,
                    cookiesData?.jwt,
                  );
                } catch (refreshError) {
                  serverLogger.error('api error refresh token failed - logging out user', {
                    error: refreshError,
                  });
                  // Refresh token is expired or invalid - log out the user
                  clearAuthentication();
                  authTool.sendAuthExpired();
                  throw refreshError; // Throw to indicate refresh failed
                }
              } else {
                serverLogger.warn('api error authenticated user missing refresh token - logging out');
                // Authenticated user without refresh token - this shouldn't happen, log out
                clearAuthentication();
                authTool.sendAuthExpired();
                return false;
              }
            } else {
              serverLogger.warn('api error no role defined - treating as expired session');
              clearAuthentication();
              authTool.sendAuthExpired();
              return false;
            }

            if (accessToken?.data?.getJwtTokenV2) {
              serverLogger.debug('api error getJwtTokenV2 success');

              if (isServer()) {
                serverLogger.debug('api error updateServerCookie');
                await updateServerCookie(
                  accessToken.data.getJwtTokenV2?.jwt,
                  accessToken.data.getJwtTokenV2?.refreshToken,
                );
              } else {
                serverLogger.debug('api error updateAppSession');
                await updateAppSession({
                  jwt: accessToken.data.getJwtTokenV2?.jwt,
                  refreshToken: accessToken.data.getJwtTokenV2.refreshToken,
                });
                // Always clear cache after updating session
                clearSessionCache();
              }
              return true; // Indicate refresh succeeded
            }

            return false; // Indicate refresh failed
          });
        }

        serverLogger.log('[http-client] token refresh result', {
          success: refreshSuccess,
          ...manager.getRefreshStats(),
          failureCount: manager.getFailureCount(),
        });

        if (refreshSuccess) {
          // Force fresh data after refresh
          const newCookiesData = isServer()
            ? await getCookieStorageFromServer()
            : await getClientSessionData({ cache: 'no-store' });
          originalRequest.headers.Authorization = `Bearer ${newCookiesData?.jwt}`;
          return httpClient(originalRequest);
        } else {
          // Refresh failed, reject the original error
          serverLogger.warn('[http-client] Token refresh failed, rejecting request');
          return Promise.reject(error);
        }
      }

      return Promise.reject(error);
    },
  );

  return httpClient;
};

export default createHttpClient;
